/**
 * Shared chart styling derived from design-system tokens (tokens/colors.css).
 * Screens never import recharts directly — they use the AreaTrend / BarCompare /
 * Donut wrappers, which pull colour + axis styling from here. Values are CSS
 * custom-property references (with hex fallbacks) so they re-theme with the app.
 */

export const CHART_COLORS = {
  pink: 'var(--spectro-pink, #EC1D8C)',
  magenta: 'var(--spectro-magenta, #D946EF)',
  purple: 'var(--spectro-purple, #9333EA)',
  violet: 'var(--spectro-violet, #3F3DFF)',
  blue: 'var(--spectro-blue, #0065FF)',
  success: 'var(--color-status-success, #16A34A)',
  warning: 'var(--color-status-warning, #D97706)',
  error: 'var(--color-status-error, #DC2626)',
  grid: 'var(--color-border-subtle, #E5EAF0)',
  axis: 'var(--color-text-tertiary, #6B7989)',
  text: 'var(--color-text-secondary, #4A5868)',
} as const;

/** Categorical series palette (donut slices, multi-series bars) — brand-forward. */
export const CHART_SERIES: readonly string[] = [
  CHART_COLORS.purple,
  CHART_COLORS.pink,
  CHART_COLORS.blue,
  CHART_COLORS.magenta,
  CHART_COLORS.violet,
  CHART_COLORS.success,
  CHART_COLORS.warning,
];

/** Pick a deterministic series colour by index (wraps around). */
export const seriesColor = (i: number): string =>
  CHART_SERIES[i % CHART_SERIES.length] ?? CHART_COLORS.purple;

/** Shared axis tick style. */
export const AXIS_TICK = { fontSize: 11, fill: CHART_COLORS.axis } as const;

/** True when the document is in RTL — charts mirror their X axis to match. */
export const isDocRtl = (): boolean =>
  typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'rtl';
